import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";
import { useDashboardSetupGuide } from "@/shared/onboarding/services/useDashboardSetupGuide";
import {
  markCelebrated,
  needsSellerToDoIt,
  readCelebrated,
} from "@/shared/onboarding/setup-progress";
import { FirstWinCelebration } from "@/shared/onboarding/components/FirstWinCelebration";

const K = keys.setupLauncher;

/** Set once the seller asks to hide the helper for good. */
const hiddenKey = (storeSlug: string) => `a2z:setup-helper-hidden:v1:${storeSlug}`;

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private browsing — a lost preference beats a crash */
  }
}

/** One line saying why the step matters, keyed by the guide's own item ids. */
const WHY: Record<string, string> = {
  categories: K.whyCategories,
  products: K.whyProducts,
  payment: K.whyPayment,
  logo: K.whyLogo,
  description: K.whyDescription,
  contact: K.whyContact,
};

const BangIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <path d="M12 7v6" />
    <path d="M12 17.2v.1" />
  </svg>
);

const CloseIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

/**
 * Floating setup helper, mounted once in the seller shell so it follows the
 * merchant off the dashboard — the setup guide used to live on one page, so
 * navigating away lost it.
 *
 * Shows ONE action at a time, in priority order (categories → products →
 * payment → logo → description → contact), not the whole checklist. Six
 * simultaneous todos is a backlog; the next single thing is an instruction.
 *
 * State comes from `useDashboardSetupGuide`, which reads real store data (has
 * categories, has products, has a logo…). It is deliberately NOT a record of
 * steps the seller was walked through: a merchant who adds categories by bulk
 * import, on another device, or before this existed is just as done.
 *
 * Disappears at 100%, and can be dismissed for good — behind a confirmation,
 * because dismissing costs the seller the walkthrough.
 */
/**
 * Is the setup helper still occupying the floating slot?
 *
 * The corner holds ONE button. This says whether setup still owns it, so the
 * shell can hand the slot to page help once the seller is through the intro —
 * see `ProtectedLayout`. Same inputs as the component's own early return, kept
 * beside it so the two cannot drift.
 */
/**
 * Who owns the floating corner: nobody yet, the setup checklist, or page help.
 *
 * THREE states, not two. The guide is derived from two queries, and until both
 * answer it reports every step as unfinished — so a boolean "is setup visible?"
 * said yes on every page load and then changed its mind, which the seller saw
 * as the yellow setup button flashing up and being replaced by the help one.
 * Showing nothing for that moment is invisible; showing the wrong thing is not.
 */
export function useFloatingHelperOwner({
  locale,
  storeSlug,
}: {
  locale: string;
  storeSlug: string;
}): "unknown" | "setup" | "help" {
  const guide = useDashboardSetupGuide({ storeSlug, locale });
  const hidden =
    typeof window === "undefined" ? false : readJson(hiddenKey(storeSlug), false);
  const items = guide.items.map((item) =>
    !guide.coreOnly && item.done && needsSellerToDoIt(storeSlug, item.id)
      ? { ...item, done: false }
      : item,
  );
  const next = items.find((item) => !item.done && item.canAct !== false);
  if (!guide.ready) return "unknown";
  return items.length > 0 && !hidden && Boolean(next) ? "setup" : "help";
}

export function SetupLauncher(props: { locale: string; storeSlug: string }) {
  return <SetupLauncherState key={props.storeSlug} {...props} />;
}

function SetupLauncherState({ locale, storeSlug }: { locale: string; storeSlug: string }) {
  const t = useTranslations();
  const guide = useDashboardSetupGuide({ storeSlug, locale });
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [progress, setProgress] = useState(() => ({
    observed: null as string | null, seen: readCelebrated(storeSlug), celebrate: false,
  }));
  const [hidden, setHidden] = useState(() =>
    typeof window === "undefined" ? false : readJson(hiddenKey(storeSlug), false),
  );

  // A step the WIZARD completed is not a step the seller can repeat: the store
  // has categories, but they have never opened the Categories page. Hold those
  // open until they do one themselves, so the helper's first instruction is
  // still "add a category".
  const items = guide.items.map((item) =>
    !guide.coreOnly && item.done && needsSellerToDoIt(storeSlug, item.id)
      ? { ...item, done: false }
      : item,
  );
  const remaining = items.filter((item) => !item.done && item.canAct !== false).length;
  const allDone = items.length > 0 && items.every((item) => item.done);
  // Priority = the guide's own order, which runs catalogue-first.
  const next = items.find((item) => !item.done && item.canAct !== false);

  /**
   * Records progress, and marks the ONE moment worth interrupting for: the
   * store being fully set up.
   *
   * There used to be a `toast.success` per newly-done step. Two problems, both
   * reported: it fired in a BURST (the ledger is only written when committed progress
   * catches a change, so every step completed since it last looked toasted at
   * once — three at a time after saving a WhatsApp number), and each one
   * duplicated the confirmation the seller had already just seen on the surface
   * they saved from. Every step's own screen confirms its own save; this has
   * nothing to add per step.
   */
  const completionSignature = `${guide.total}:${guide.items.filter(item => item.done).map(item => item.id).join("|")}`;
  if (guide.ready && guide.total > 0 && progress.observed !== completionSignature) {
    // Include celebrations owned by actual create surfaces, not just this helper.
    const seen = [...new Set([...progress.seen, ...readCelebrated(storeSlug)])];
    const newlyDone = guide.items.filter(item => item.done && !seen.includes(item.id));
    const nextSeen = [...seen, ...newlyDone.map(item => item.id)];
    // An already-configured first authoritative read stays silent. Incomplete
    // query snapshots must not record a partial ledger before that first read.
    const firstRun = seen.length === 0 && newlyDone.length > 1;
    setProgress({
      observed: completionSignature, seen: nextSeen,
      // Core (Free) stores never get the full-screen overlay: their guide has
      // ONE step, so a fresh device read it as "just finished" and stacked
      // "your store is ready" on top of the first-load prompts. The Core
      // dashboard says it instead, as a card (see CoreDashboard).
      celebrate:
        !guide.coreOnly &&
        (progress.celebrate || (newlyDone.length > 0 && !firstRun && nextSeen.length >= guide.total)),
    });
  }
  useEffect(() => {
    if (guide.ready && progress.seen.length > 0) markCelebrated(storeSlug, progress.seen);
  }, [guide.ready, progress.seen, storeSlug]);

  // The overlay outlives the launcher: finishing the last step is exactly when
  // `allDone` flips and this component would otherwise return null.
  const storeReady = progress.celebrate ? (
    <FirstWinCelebration
      win="store"
      onClose={() => setProgress(previous => ({ ...previous, celebrate: false }))}
    />
  ) : null;

  // `!guide.ready` first: until both queries answer, every step reads as
  // unfinished, so drawing now means drawing the yellow button on every page
  // load and taking it away again once the answers arrive.
  if (!guide.ready || allDone || items.length === 0 || hidden || !next)
    return storeReady;
  // The Core dashboard carries its one setup step itself ("أضف منتجك الأول",
  // at the top). The floating button there said the same thing a second time
  // and, on a phone, sat on the "اليوم" card's cash-with-courier amount.
  if (guide.coreOnly && /\/dashboard\/?$/.test(pathname)) return storeReady;

  const confirmHide = () => {
    write(hiddenKey(storeSlug), true);
    setHidden(true);
  };

  return (
    <>
      {storeReady}
      {open ? (
        <>
          {/* Click-away. Below the bottom nav (z-40) and every dialog (z-50) —
              this is page furniture, not a modal. */}
          <div
            className="fixed inset-0 z-20"
            onClick={() => {
              setOpen(false);
              setConfirming(false);
            }}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-label={t(K.helpAria)}
            className="fixed bottom-[calc(9.5rem+env(safe-area-inset-bottom))] end-4 z-30 flex w-[min(19rem,calc(100vw-2rem))] flex-col gap-2 rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-4 shadow-2xl lg:bottom-24"
          >
            {confirming ? (
              // Dismissing costs the seller the walkthrough, so it is worth one
              // question — but only one, and "keep it" is the default action.
              <>
                <span className="text-[13.5px] font-bold text-[color:var(--text-primary)]">
                  {t(K.confirmTitle)}
                </span>
                <span className="text-[12px] leading-relaxed text-[color:var(--text-secondary)]">
                  {t(K.confirmBody)}
                </span>
                <div className="mt-1 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={confirmHide}
                    className="rounded-lg px-2.5 py-1.5 text-[12px] font-semibold text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface-alt)]"
                  >
                    {t(K.confirmYes)}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    className="rounded-lg bg-[color:var(--primary-500)] px-3 py-1.5 text-[12.5px] font-bold text-[color:var(--on-accent)]"
                  >
                    {t(K.confirmNo)}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[10.5px] font-bold uppercase tracking-wide text-[color:var(--gold-ink,var(--text-muted))]">
                    {remaining === 1 ? t(K.lastOne) : t(K.nextEyebrow)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setConfirming(true)}
                    aria-label={t(K.dismiss)}
                    className="-me-1 -mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface-alt)]"
                  >
                    <CloseIcon />
                  </button>
                </div>

                <span className="text-[15px] font-bold leading-snug text-[color:var(--text-primary)]">
                  {next.title}
                </span>
                <span className="text-[12px] leading-relaxed text-[color:var(--text-secondary)]">
                  {t(WHY[next.id] ?? K.nextEyebrow)}
                </span>

                <Link
                  to={next.href}
                  onClick={() => setOpen(false)}
                  className="mt-1 flex min-h-[42px] items-center justify-center gap-2 rounded-xl bg-[color:var(--primary-500)] px-4 text-[13.5px] font-bold text-[color:var(--on-accent)]"
                >
                  {t(K.start)}
                  <span className="flex rtl:-scale-x-100">
                    <ArrowIcon />
                  </span>
                </Link>

                {remaining > 1 ? (
                  <span className="text-center text-[11px] text-[color:var(--text-muted)]">
                    {t(K.remaining, { count: remaining })}
                  </span>
                ) : null}
              </>
            )}
          </div>
        </>
      ) : null}

      {/* Labelled, not a bare "!" (roundtable 2, B7): the seller reads
          "خطوة متبقية" instead of guessing at a mark. Clear of the bottom nav
          on mobile — the nav is ~86px tall with its safe-area padding — and
          `end` so it mirrors in RTL. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t(K.helpAria)}
        aria-expanded={open}
        // Trioty brand gold, not `--primary-500`: primary inverts per theme
        // (near-black in light, near-white in dark), so the button read as a
        // plain white disc on the dark dashboard. The brand gold is the same in
        // both themes, which is what an attention cue needs — and dark ink on
        // it clears AA comfortably.
        className="setup-fab fixed bottom-[calc(6.5rem+env(safe-area-inset-bottom))] end-4 z-30 flex h-11 items-center gap-1.5 rounded-full bg-[#FAB95B] pe-4 ps-3 text-[13px] font-bold text-[#1a1a19] shadow-lg lg:bottom-6"
      >
        <BangIcon />
        <span aria-hidden="true">{t(K.fabLabel, { count: remaining })}</span>
      </button>
    </>
  );
}
